import {Platform, Vibration} from 'react-native';
import {getPosPreference} from './posPreferences';

/**
 * haptics.js
 *
 * ONE place for tactile feedback in every module of the app. Screens and shared
 * components call the semantic helpers (`haptics.select()`, `haptics.success()`…)
 * and never talk to expo-haptics / Vibration themselves.
 *
 * Semantics (keep to these so the app "feels" consistent, and so feedback stays
 * meaningful instead of buzzing on every touch):
 *   - select  : the user moved between options / tabs / chips / switches.
 *   - tap     : a deliberate primary press (add to cart, confirm, send).
 *   - medium  : a gesture committed (swipe action, drag drop, long-press menu).
 *   - heavy   : a significant, hard-to-undo moment (payment captured, delete).
 *   - success : an operation finished well.
 *   - warning : a destructive confirmation is about to happen / needs attention.
 *   - error   : an operation failed or input was rejected.
 *
 * Do NOT fire on plain list taps, scrolling or text entry.
 *
 * Safety:
 *   - expo-haptics is a native module. Its NATIVE half (`ExpoHaptics`) is looked
 *     up in Expo's module registry at call time — the JS package is NEVER
 *     `require`d. A lazy `try { require('expo-haptics') }` looks safe but is not
 *     (HAPPY-APP-1P, 4.13.4 Android, fatal on the first tap): Metro treats a
 *     require inside `try` as OPTIONAL, so a bundle built without the package in
 *     node_modules ships a dangling module id, and at runtime Metro's
 *     `guardedLoadModule` hands the "Requiring unknown module" error straight to
 *     `ErrorUtils.reportFatalError` — the surrounding catch never sees it. The
 *     same applies to the JS package's own `requireNativeModule` throwing on an
 *     older binary. The registry lookup has no bundle dependency at all, so a
 *     missing package / native module simply yields `null`. Keep `expo-haptics`
 *     in package.json: autolinking still needs it to compile the native module.
 *   - Web (incl. the Tauri / Electron desktop builds) is a no-op.
 *   - Android without the native module falls back to a very short Vibration pulse;
 *     iOS has no usable fallback (Vibration ignores the duration) so it stays silent.
 *   - Honours the user's "Haptic feedback" setting (posPreferences.hapticsEnabled)
 *     and drops pulses closer together than {@link HAPTIC_MIN_GAP_MS}.
 */

/** Pulses closer together than this are dropped (a rapid tap burst should not buzz continuously). */
export const HAPTIC_MIN_GAP_MS = 40;

/** @type {ReadonlyArray<string>} */
export const HAPTIC_KINDS = Object.freeze(['select', 'tap', 'medium', 'heavy', 'success', 'warning', 'error']);

/** Android Vibration fallback length (ms) per kind, used only when expo-haptics is unavailable. */
const FALLBACK_MS = Object.freeze({select: 6, tap: 10, medium: 16, heavy: 28, success: 14, warning: 22, error: 34});

/** Name the expo-haptics native module registers under in `globalThis.expo.modules`. */
export const HAPTICS_NATIVE_MODULE_NAME = 'ExpoHaptics';

/**
 * Raw argument the native `impactAsync` / `notificationAsync` take per kind —
 * the string values behind expo-haptics' `ImpactFeedbackStyle` /
 * `NotificationFeedbackType` enums (the native module does not expose the enums).
 */
const NATIVE_CALL = Object.freeze({
    select: {method: 'selectionAsync'},
    tap: {method: 'impactAsync', arg: 'light'},
    medium: {method: 'impactAsync', arg: 'medium'},
    heavy: {method: 'impactAsync', arg: 'heavy'},
    success: {method: 'notificationAsync', arg: 'success'},
    warning: {method: 'notificationAsync', arg: 'warning'},
    error: {method: 'notificationAsync', arg: 'error'},
});

let nativeModule;

/**
 * Looks the expo-haptics native module up in Expo's JSI module registry — the
 * same lookup `requireOptionalNativeModule('ExpoHaptics')` performs, without
 * importing anything into the bundle (see the Safety note above for why a
 * `require` is not an option). Pure apart from the property reads, so it is
 * unit-testable with a fake global.
 *
 * @param {object} [root=globalThis] Object carrying the `expo.modules` registry.
 * @returns {?object} The native module, or `null` when this binary lacks it.
 */
export const resolveHapticsNativeModule = (root = globalThis) => {
    try {
        const mod = root?.expo?.modules?.[HAPTICS_NATIVE_MODULE_NAME];

        return mod && typeof mod === 'object' ? mod : null;
    } catch (_error) {
        return null;
    }
};

/**
 * Lazily resolves (and caches) the expo-haptics native module; `null` when the
 * binary does not contain it.
 *
 * @returns {?object}
 */
const getNative = () => {
    if (nativeModule === undefined) {
        nativeModule = resolveHapticsNativeModule();
    }

    return nativeModule;
};

/**
 * Whether a pulse should fire now. Pure, so it is unit-testable.
 *
 * @param {object} args
 * @param {boolean} args.enabled User setting.
 * @param {string} args.platform `Platform.OS`.
 * @param {number} args.now Current time (ms).
 * @param {number} args.last Time of the last fired pulse (ms).
 * @param {number} [args.minGap=HAPTIC_MIN_GAP_MS]
 * @returns {boolean}
 */
export const shouldFireHaptic = ({enabled, platform, now, last, minGap = HAPTIC_MIN_GAP_MS}) => {
    if (!enabled || platform === 'web') {
        return false;
    }

    return now - last >= minGap;
};

let lastFiredAt = 0;

/**
 * Fires a haptic pulse of the given kind. Never throws, never blocks.
 *
 * @param {('select'|'tap'|'medium'|'heavy'|'success'|'warning'|'error')} kind
 * @returns {boolean} Whether a pulse was attempted.
 */
export const triggerHaptic = (kind) => {
    const now = Date.now();

    if (!HAPTIC_KINDS.includes(kind) || !shouldFireHaptic({
        enabled: getPosPreference('hapticsEnabled'),
        platform: Platform.OS,
        now,
        last: lastFiredAt,
    })) {
        return false;
    }

    lastFiredAt = now;
    const native = getNative();

    try {
        const call = NATIVE_CALL[kind];

        if (native && typeof native[call.method] === 'function') {
            const pending = call.arg === undefined ? native[call.method]() : native[call.method](call.arg);
            pending?.catch?.(() => {});

            return true;
        }

        if (Platform.OS === 'android' && typeof Vibration?.vibrate === 'function') {
            Vibration.vibrate(FALLBACK_MS[kind]);

            return true;
        }
    } catch (_error) {
        // Haptics are decoration: never let them break a press handler.
    }

    return false;
};

/**
 * Test helper: injects the native module (`null` simulates a binary without
 * the ExpoHaptics native module) and resets the throttle clock.
 *
 * @param {?object} [mod] Fake module, `null` for "not installed", `undefined` to re-resolve.
 */
export const setHapticsNativeForTests = (mod) => {
    nativeModule = mod;
    lastFiredAt = 0;
};

/**
 * Wraps a press handler so it fires a haptic first. Keeps shared components
 * one-liners: `onPress={withHaptic(onPress, 'tap')}`.
 *
 * @param {?function} handler Original handler (may be undefined).
 * @param {('select'|'tap'|'medium'|'heavy'|'success'|'warning'|'error')} [kind='select']
 * @returns {function} Handler that vibrates, then calls `handler` with the same arguments.
 */
export const withHaptic = (handler, kind = 'select') => (...args) => {
    triggerHaptic(kind);

    return handler?.(...args);
};

export const haptics = {
    select: () => triggerHaptic('select'),
    tap: () => triggerHaptic('tap'),
    medium: () => triggerHaptic('medium'),
    heavy: () => triggerHaptic('heavy'),
    success: () => triggerHaptic('success'),
    warning: () => triggerHaptic('warning'),
    error: () => triggerHaptic('error'),
};

export default haptics;
